import { useEffect, useState } from "react";
import { api, fmtEUR, fmtDate, RECEIPT_STATUS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Receipt as ReceiptIcon, Download, Pencil } from "lucide-react";
import { toast } from "sonner";

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Legally, a "quittance" is issued only when the full amount is paid.
// A partial payment produces a "reçu" (simple receipt of partial payment).
function docTypeLabel(r) {
  const paid = Number(r.paid_amount || 0);
  const total = Number(r.total_amount || 0);
  const isFull = r.status === "paye" && paid + 0.01 >= total;
  return isFull ? "Quittance" : "Reçu";
}

export default function ReceiptsPage() {
  const { user } = useAuth();
  const { canWrite, isDemo } = planContext(user);
  const [items, setItems] = useState([]);
  const [leases, setLeases] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ lease_id: "", period: currentPeriod(), status: "paye", paid_amount: "", caf_amount: "" });

  const load = async () => {
    const [r, l] = await Promise.all([api.get("/receipts"), api.get("/leases")]);
    setItems(r.data); setLeases(l.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    if (!canWrite) return;
    setEditId(null);
    setForm({ lease_id: "", period: currentPeriod(), status: "paye", paid_amount: "", caf_amount: "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    if (!canWrite) return;
    setEditId(r.id);
    const caf = Number(r.caf_amount || 0);
    const total = r.paid_amount;
    setForm({
      lease_id: r.lease_id,
      period: r.period,
      status: r.status,
      // Champ "versement locataire" = total encaissé - part CAF
      paid_amount: total == null ? "" : String(Number(total) - caf),
      caf_amount: caf ? String(caf) : "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!canWrite) return;
    try {
      const caf = form.caf_amount === "" ? 0 : Number(form.caf_amount);
      const tenant = form.paid_amount === "" ? null : Number(form.paid_amount);
      const payload = {
        lease_id: form.lease_id,
        period: form.period,
        status: form.status,
        // Montant total encaissé = versement locataire + versement CAF.
        // Si le versement locataire est vide → null (le backend pré-remplit le total dû).
        paid_amount: tenant === null ? null : tenant + caf,
        caf_amount: caf || null,
      };
      if (editId) {
        await api.put(`/receipts/${editId}`, payload); toast.success("Quittance mise à jour");
      } else {
        await api.post("/receipts", payload); toast.success("Quittance créée");
      }
      setOpen(false); load();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const updateStatus = async (r, status) => {
    if (!canWrite) return;
    try {
      await api.put(`/receipts/${r.id}`, {
        lease_id: r.lease_id, period: r.period, status,
        paid_amount: r.paid_amount, caf_amount: r.caf_amount ?? null, paid_at: r.paid_at,
      });
      toast.success("Statut mis à jour"); load();
    } catch (e) { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer cette quittance ?")) return;
    await api.delete(`/receipts/${id}`); toast.success("Supprimée"); load();
  };

  const downloadPdf = async (id) => {
    if (isDemo) return;
    try {
      const res = await api.get(`/receipts/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `quittance_${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors du téléchargement"); }
  };

  const statusBadge = (s) => {
    const m = RECEIPT_STATUS.find((x) => x.value === s) || RECEIPT_STATUS[1];
    return <Badge className={`${m.color} border-0 font-medium`}>{m.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Quittances</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} quittance(s) générée(s).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="receipt-new-btn" onClick={openNew} disabled={!canWrite} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle quittance
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Générer une quittance</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Bail</Label>
                <Select value={form.lease_id} onValueChange={(v) => setForm({ ...form, lease_id: v })}>
                  <SelectTrigger data-testid="receipt-lease"><SelectValue placeholder="Sélectionner un bail" /></SelectTrigger>
                  <SelectContent>
                    {leases.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.property_label} — {l.tenant_name} ({fmtEUR(Number(l.rent_amount) + Number(l.charges_amount))})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Période (YYYY-MM)</Label>
                <Input data-testid="receipt-period" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026-02" />
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="receipt-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECEIPT_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Versement du locataire (€) — laisser vide pour total automatique</Label>
                <Input data-testid="receipt-tenant-amount" type="number" min="0" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} />
              </div>
              <div>
                <Label>Versement de la CAF (€) — optionnel</Label>
                <Input data-testid="receipt-caf-amount" type="number" min="0" value={form.caf_amount} onChange={(e) => setForm({ ...form, caf_amount: e.target.value })} placeholder="0" />
                <p className="mt-1 text-xs text-slate-500">S'additionne au versement du locataire (tiers payant APL/CAF). Laisser vide si aucun.</p>
              </div>
              {(() => {
                const selected = leases.find((l) => l.id === form.lease_id);
                const dueTotal = selected ? Number(selected.rent_amount || 0) + Number(selected.charges_amount || 0) : 0;
                const auto = form.paid_amount === "";
                const tenantPart = auto ? Math.max(dueTotal - Number(form.caf_amount || 0), 0) : Number(form.paid_amount || 0);
                const received = tenantPart + Number(form.caf_amount || 0);
                if (received <= 0) return null;
                return (
                  <div data-testid="receipt-total-received" className="rounded-lg bg-[hsl(var(--primary))]/8 px-3 py-2 text-sm flex items-center justify-between">
                    <span className="text-slate-600">Total encaissé{auto ? " (total automatique)" : ""}</span>
                    <span className="font-semibold text-slate-900">{fmtEUR(received)}</span>
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button data-testid="receipt-save" onClick={save} disabled={!form.lease_id} className="bg-[hsl(var(--primary))]">{editId ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <ReceiptIcon className="h-10 w-10 mx-auto text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">Aucune quittance générée.</div>
          </div>
        ) : (
          <>
          <Table className="hidden sm:table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Document</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Bien</TableHead>
                <TableHead>Locataire</TableHead>
                <TableHead>Total dû</TableHead>
                <TableHead>Reçu</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => {
                const label = docTypeLabel(r);
                return (
                <TableRow key={r.id} data-testid={`receipt-row-${r.id}`}>
                  <TableCell>
                    <Badge className={`${label === "Quittance" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} border-0`}>
                      {label}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{r.period}</TableCell>
                  <TableCell className="text-sm">{r.property_label}</TableCell>
                  <TableCell className="text-sm">{r.tenant_name}</TableCell>
                  <TableCell className="text-sm">{fmtEUR(r.total_amount)}</TableCell>
                  <TableCell className="text-sm">
                    {fmtEUR(r.paid_amount)}
                    {Number(r.caf_amount || 0) > 0 && (
                      <span className="block text-xs text-slate-400" data-testid={`receipt-caf-${r.id}`}>
                        dont CAF {fmtEUR(r.caf_amount)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={(v) => updateStatus(r, v)}>
                      <SelectTrigger className="w-[140px] h-8 border-0 p-0 bg-transparent shadow-none focus:ring-0" data-testid={`receipt-status-${r.id}`}>
                        <SelectValue asChild>{statusBadge(r.status)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {RECEIPT_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" disabled={isDemo} onClick={() => downloadPdf(r.id)} data-testid={`receipt-pdf-${r.id}`}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => openEdit(r)} data-testid={`receipt-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Vue mobile : cartes empilées */}
          <div className="sm:hidden divide-y divide-slate-100">
            {items.map((r) => {
              const label = docTypeLabel(r);
              return (
              <div key={r.id} className="p-4" data-testid={`receipt-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Badge className={`${label === "Quittance" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} border-0`}>{label}</Badge>
                    <div className="mt-1 font-medium">{r.period}</div>
                  </div>
                  <Select value={r.status} onValueChange={(v) => updateStatus(r, v)}>
                    <SelectTrigger className="w-[130px] h-8 border-0 p-0 bg-transparent shadow-none focus:ring-0" data-testid={`receipt-status-m-${r.id}`}>
                      <SelectValue asChild>{statusBadge(r.status)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {RECEIPT_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-y-1 text-sm">
                  <div><span className="text-slate-500">Bien : </span>{r.property_label}</div>
                  <div><span className="text-slate-500">Locataire : </span>{r.tenant_name}</div>
                  <div><span className="text-slate-500">Total dû : </span>{fmtEUR(r.total_amount)} · <span className="text-slate-500">Reçu : </span>{fmtEUR(r.paid_amount)}{Number(r.caf_amount || 0) > 0 && <span className="text-slate-400"> (dont CAF {fmtEUR(r.caf_amount)})</span>}</div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="outline" size="sm" className="flex-1" disabled={isDemo} onClick={() => downloadPdf(r.id)} data-testid={`receipt-pdf-m-${r.id}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                  <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => openEdit(r)} data-testid={`receipt-edit-m-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => remove(r.id)} data-testid={`receipt-delete-m-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
              );
            })}
          </div>
          </>
        )}
      </Card>
    </div>
  );
}
